/**
 * /team/overdue-documents — manager-scoped list of overdue document
 * assignments for the caller's direct reports.
 *
 * This page is the Review destination for the
 * ``code: "overdue_documents"`` alert on /my/team. The previous
 * ``action_path: "/documents"`` led to the HR-admin uploaded-file
 * table (wrong model, wrong scope), so managers saw "2 documents
 * overdue" → click → empty grid. Now Review lands here, on a page
 * whose API call is guaranteed by the backend to return the same
 * set the alert counted.
 *
 * Manager actions per row:
 *   * "Open" — deep-link to the employee's profile so they can chase
 *     the report directly.
 *   * "Remind" — fires ``POST /document-assignments/{id}/remind`` to
 *     trigger the standard reminder email. Disabled (with a tooltip)
 *     once a reminder has already gone out in the last 24h to prevent
 *     accidental spam — that gate lives on the backend but we
 *     pre-disable the button on stale ``reminded_at`` so the UI doesn't
 *     flash a button you can't actually use.
 *
 * Empty state: this is where managers land after a click, so a
 * confident empty state matters — "Nothing overdue right now."
 * is the right vibe rather than the cold "No results."
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Bell, ArrowRight } from "lucide-react";
import { pageTransition, fullName } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  listTeamOverdueDocuments,
  remindAssignment,
  type DocumentAssignment,
} from "@/api/documentAssignments";

function daysOverdue(deadlineIso: string | null): number {
  if (!deadlineIso) return 0;
  const deadline = new Date(deadlineIso).getTime();
  const now = Date.now();
  return Math.max(0, Math.floor((now - deadline) / (1000 * 60 * 60 * 24)));
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * True when a reminder went out in the last 24h. We mirror the
 * backend's spam guard so the button reads disabled instead of
 * showing "remind →" only to 429 a second later.
 */
function remindedRecently(a: DocumentAssignment): boolean {
  if (!a.reminded_at) return false;
  const last = new Date(a.reminded_at).getTime();
  return Date.now() - last < 24 * 60 * 60 * 1000;
}

export function TeamOverdueDocsPage() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<DocumentAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listTeamOverdueDocuments();
      setRows(data);
    } catch (err) {
      setLoadError(
        err instanceof Error
          ? err.message
          : "We couldn't load your team's overdue documents.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRemind = async (a: DocumentAssignment) => {
    setBusyId(a.id);
    try {
      const res = await remindAssignment(a.id);
      addToast(
        "success",
        `Reminder sent · ${res.reminder_count} total`,
      );
      // Refresh so reminded_at updates and the button locks for 24h.
      await load();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Couldn't send the reminder. Try again in a moment.";
      addToast("error", msg);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Overdue documents"
        subtitle={
          loading
            ? "Loading…"
            : `${rows.length} document${rows.length === 1 ? "" : "s"} overdue on your team`
        }
      />

      {loadError ? (
        <Card>
          <div className="flex items-start gap-3 text-sm">
            <AlertCircle className="h-5 w-5 text-danger mt-0.5" />
            <div>
              <p className="font-medium text-text-primary">
                Couldn't load overdue documents
              </p>
              <p className="text-text-muted mt-0.5">{loadError}</p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-3"
                onClick={load}
              >
                Retry
              </Button>
            </div>
          </div>
        </Card>
      ) : !loading && rows.length === 0 ? (
        <Card className="text-center py-12">
          <CheckCircle2 className="h-8 w-8 text-success mx-auto mb-2" />
          <p className="text-text-primary font-medium">
            Nothing overdue right now.
          </p>
          <p className="text-sm text-text-muted mt-1">
            Your team is on top of their paperwork.
          </p>
          <Link
            to="/my/team"
            className="text-sm text-accent hover:underline inline-flex items-center gap-1 mt-4"
          >
            Back to My Team <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-text-muted">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3 font-medium">Employee</th>
                  <th className="py-2 pr-3 font-medium">Document</th>
                  <th className="py-2 pr-3 font-medium">Deadline</th>
                  <th className="py-2 pr-3 font-medium">Overdue by</th>
                  <th className="py-2 pl-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const overdueDays = daysOverdue(a.deadline_at);
                  const recentlyReminded = remindedRecently(a);
                  return (
                    <tr
                      key={a.id}
                      className="border-b border-border last:border-0"
                    >
                      <td className="py-3 pr-3 text-text-primary">
                        {a.employee ? (
                          <Link
                            to={`/employees/${a.employee.id}`}
                            className="hover:underline"
                          >
                            {fullName(
                              a.employee.first_name,
                              a.employee.last_name,
                            )}
                          </Link>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-text-primary">
                        {a.template?.name || "—"}
                      </td>
                      <td className="py-3 pr-3 text-text-muted">
                        {formatDate(a.deadline_at)}
                      </td>
                      <td className="py-3 pr-3">
                        <Badge variant="danger">
                          {overdueDays} day{overdueDays === 1 ? "" : "s"}
                        </Badge>
                      </td>
                      <td className="py-3 pl-3 text-right">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleRemind(a)}
                          disabled={
                            busyId === a.id || recentlyReminded
                          }
                          title={
                            recentlyReminded
                              ? "A reminder was sent in the last 24 hours"
                              : undefined
                          }
                        >
                          <Bell className="h-3.5 w-3.5 mr-1" />
                          {busyId === a.id ? "Sending…" : "Remind"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </motion.div>
  );
}
